import { IColorPickerResult } from "../../libs/Picker/types";
import { IConfig } from "../configs/types";
import { IFieldInputData, IStyleInput } from "../fields/boomtypes";
import { IRule } from "../logic/types";
import { IError } from "../types/errors";
import { ActionType } from "../types/store-types";
import { IColorData } from "../ui/types";

export interface IFieldIcons {
    styles: { input: IInputWithHoverColor; inputValue: IFieldInputData["styles"]["inputValue"] };
}
export interface IFont {
    family: string;
    variants: string[];
    subsets: string[];
    version: string;
    files: {
        [key: string]: string;
    };
    category: string;
    kind: string;
}

interface IInputWithHoverColor extends IStyleInput {
    hoverColor: IColorData;
}
export interface IFormStyles {
    page: {
        widthMeasurement: "Px" | "Percent";
        alignment: string;
        fill: {
            color: IColorData;
        };
        border: {
            radius: number;
            size: number;
            color: IColorData;
        };
    };
    background: {
        color: IColorPickerResult;
    };
    buttons: {
        submit: {
            input: IFieldInputData;
        };
        next: {
            input: IFieldInputData;
            icon: IFieldIcons;
        };
        back: {
            input: IFieldInputData;
            icon: IFieldIcons;
        };
    };
}

export interface IFormSettings {
    native: {
        currency: string;
        priceFormat: string;
    };
}

export enum PageStyleChangeTypes {
    BORDER_RADIUS,
    BORDER_SIZE,
    BORDER_COLOR,
    FILL,
}

export interface IForm {
    _id: string;
    name: string;
    status: number;
    createdAt?: string;
    updatedAt?: string;

    integrations?: {
        hubspot?: {
            table: string;
            fields: Record<string, any>;
            _id: string;
        };
        googleSheet?: {
            worksheet: string;
            fields: Record<string, string>;
            _id: string;
        };
        mailChimp?: {
            fields: Record<string, string>;
            listId: string;
            _id: string;
        };
    };
    styles: IFormStyles;
}

/* - Data Types - */

/* -- Get Forms Request Data --  */

export interface IGetFormsRequestData {}

export interface IGetFormsSuccessData {
    ownerId: string;
}

export interface IGetFormsFailureData extends IError {}

/* -- Get Form Request Data --  */

export interface IGetFormRequestData {
    id: IForm["_id"];
}

export interface IGetFormSuccessData extends IGetFormsSuccessData {}

export interface IGetFormFailureData extends IError {}

/* -- Create Form Request Data --  */

export interface ICreateFormRequestData {
    name: string;
    config?: Partial<IConfig>;
}

export interface ICreateFormSuccessData {}

export interface ICreateFormFailureData extends IError {}

/* -- Update Form Name Request Data --  */

export interface IUpdateFormNameRequestData {
    name: string;
}

export interface IUpdateFormNameSuccessData {}

export interface IUpdateFormNameFailureData extends IError {}

/* -- Update Form Name Request Data --  */

export interface IUpdateFormBackgroundRequestData {
    prevState: IFormStyles["background"];
    background: IFormStyles["background"];
}

export interface IUpdateFormPageStylesRequestData {
    prevState: IFormStyles["page"];
    page: IFormStyles["page"];
    type: PageStyleChangeTypes;
}

export interface IUpdateFormBackgroundSuccessData {}
export interface IUpdateFormPageStylesSuccessData {}

export interface IUpdateFormBackgroundFailureData extends IError {}
export interface IUpdateFormPageStylesFailureData extends IError {}

export interface IUpdateFormNameSuccessData {}

export interface IUpdateFormNameFailureData extends IError {}

export interface ISetCurrentForm extends IForm {}

/* -- Get User Fonts Request Data --  */
export interface ISetInitialFonts {
    fonts: IFont[];
}

export interface IGetUserFontsRequestData {}
export interface IGetUserFontsResponseData {
    fonts: IFont[];
}

export interface IGetUserFontsSuccessData {
    fonts: IFont[];
}

export interface IGetUserFontsFailureData extends IError {}

/* -- Get User Fonts Request Data --  */

export interface ISaveChangedFontsRequestData {
    font: IFont[];
}

export interface ISetCurrentForm extends IForm {}

export interface ISetFormStyles extends IFormStyles {}

export interface ISaveChangedFontsSuccessData {}

export interface ISaveChangedFontsFailureData extends IError {}

/*  For Form Settings  */

export interface IInitFormSetting {
    native: IFormSettings;
}
export interface ISetFormSettingsRequest {
    prevState: IFormSettings;
    currentState: IFormSettings;
}

export interface ISetFormSettingsSuccess {}
export interface ISetFormSettingsFailure extends IError {
    prevState: IFormSettings;
}

/* - State - */

export interface IFormsState {
    current: IForm | null;
    fonts: IFont[];
    error: IError | null;
    isLoading: boolean;
    styles: IFormStyles;
    settings: IFormSettings;
    ownerId: string;
    transfer?: ITransferFormData;
    isTransfering: boolean;
}

interface IUpdateFormButtonsDataRequest {
    prevState: IFormStyles["buttons"];
    currentState: IFormStyles["buttons"];
}

interface IInitButtonsStyles {
    [key: string]: any;
}

interface ITransferForm {
    instance: string;
    compId: string;
}
interface ITransferFormData {
    fields: any[];
    pages: {
        [key: string]: number[];
    };
    logic: IRule[];
    wixFields: { id: number; type: string }[];
}

interface ITransferFormSubmissions extends ITransferForm {
    formId: string;
    fieldsDictionary: { [key: number]: string };
}
interface ITransferLoading {
    isLoading: boolean;
}

/* - Action Types - */

export enum FormsActionTypes {
    GET_FORMS_REQUEST = "FORMS.GET_FORMS_REQUEST",
    GET_FORMS_SUCCESS = "FORMS.GET_FORMS_SUCCESS",
    GET_FORMS_FAILURE = "FORMS.GET_FORMS_FAILURE",

    GET_FORM_REQUEST = "FORMS.GET_FORM_REQUEST",
    GET_FORM_SUCCESS = "FORMS.GET_FORM_SUCCESS",
    GET_FORM_FAILURE = "FORMS.GET_FORM_FAILURE",

    CREATE_FORM_REQUEST = "FORMS.CREATE_FORM_REQUEST",
    CREATE_FORM_SUCCESS = "FORMS.CREATE_FORM_SUCCESS",
    CREATE_FORM_FAILURE = "FORMS.CREATE_FORM_FAILURE",

    UPDATE_FORM_NAME_REQUEST = "FORMS.UPDATE_FORM_NAME_REQUEST",
    UPDATE_FORM_NAME_SUCCESS = "FORMS.UPDATE_FORM_NAME_SUCCESS",
    UPDATE_FORM_NAME_FAILURE = "FORMS.UPDATE_FORM_NAME_FAILURE",

    UPDATE_FORM_BACKGROUND_REQUEST = "FORMS.UPDATE_FORM_BACKGROUND_REQUEST",
    UPDATE_FORM_BACKGROUND_SUCCESS = "FORMS.UPDATE_FORM_BACKGROUND_SUCCESS",
    UPDATE_FORM_BACKGROUND_FAILURE = "FORMS.UPDATE_FORM_BACKGROUND_FAILURE",

    UPDATE_FORM_PAGE_STYLES_REQUEST = "FORMS.UPDATE_FORM_PAGE_STYLES_REQUEST",
    UPDATE_FORM_PAGE_STYLES_SUCCESS = "FORMS.UPDATE_FORM_PAGE_STYLES_SUCCESS",
    UPDATE_FORM_PAGE_STYLES_FAILURE = "FORMS.UPDATE_FORM_PAGE_STYLES_FAILURE",

    SET_CURRENT_FORM = "FORMS.SET_CURRENT_FORM",

    SET_FORM_STYLES = "FORMS.SET_FORM_STYLES",

    SET_INITIAL_FONTS = "FORMS.SET_INITIAL_FONTS",

    GET_USER_FONTS_REQUEST = "FORMS.GET_USER_FONTS_REQUEST",
    GET_USER_FONTS_SUCCESS = "FORMS.GET_USER_FONTS_SUCCESS",
    GET_USER_FONTS_FAILURE = "FORMS.GET_USER_FONTS_FAILURE",

    SAVE_CHANGED_FONTS_REQUEST = "FORMS.SAVE_CHANGED_FONTS_REQUEST",
    SAVE_CHANGED_FONTS_SUCCESS = "FORMS.SAVE_CHANGED_FONTS_SUCCESS",
    SAVE_CHANGED_FONTS_FAILURE = "FORMS.SAVE_CHANGED_FONTS_FAILURE",

    ADD_BULK_USER_FONTS_REQUEST = "FORMS.ADD_BULK_SAVE_USER_FONTS_REQUEST",
    ADD_BULK_USER_FONTS_SUCCESS = "FORMS.ADD_BULK_SAVE_USER_FONTS_SUCCESS",
    ADD_BULK_USER_FONTS_FAILURE = "FORMS.ADD_BULK_SAVE_USER_FONTS_FAILURE",

    INIT_FORM_SETTINGS = "FORMS.INIT_FORM_SETTINGS",
    SET_FORM_SETTINGS_REQUEST = "FORMS.SET_FORM_SETTINGS_REQUEST",
    SET_FORM_SETTINGS_SUCCESS = "FORMS.SET_FORM_SETTINGS_SUCCESS",
    SET_FORM_SETTINGS_FAILURE = "FORMS.SET_FORM_SETTINGS_FAILURE",

    INIT_FORM_BUTTONS_STYLES = "FORMS.INIT_BUTTONS_STYLES",
    UPDATE_FORM_BUTTONS_DATA_REQUEST = "FORMS.UPDATE_FORM_BUTTONS_DATA_UPDATE_REQUEST",

    TRANSFER_FORM = "FORMS.TRANSFER_FORM",
    SET_TRANSFER_FORM_DATA = "FORMS.SET_TRANSFER_FORM_DATA",
    TRANSFER_FORM_SUBMISSIONS = "FORMS.TRANSFER_FORM_SUBMISSIONS",
    SET_TRANSFER_LOADING = "FORMS.TRANSFER_FORM_LOADING",
}

/* - Action Interfaces  - */

export type InitFormButtonsStylesActionType = ActionType<FormsActionTypes.INIT_FORM_BUTTONS_STYLES, IInitButtonsStyles>;

export type GetFormsRequestActionType = ActionType<FormsActionTypes.GET_FORMS_REQUEST, IGetFormsRequestData>;
export type GetFormsSuccessActionType = ActionType<FormsActionTypes.GET_FORMS_SUCCESS, IGetFormsSuccessData>;
export type GetFormsFailureActionType = ActionType<FormsActionTypes.GET_FORMS_FAILURE, IGetFormsFailureData>;

export type GetFormRequestActionType = ActionType<FormsActionTypes.GET_FORM_REQUEST, IGetFormRequestData>;
export type GetFormSuccessActionType = ActionType<FormsActionTypes.GET_FORM_SUCCESS, IGetFormSuccessData>;
export type GetFormFailureActionType = ActionType<FormsActionTypes.GET_FORM_FAILURE, IGetFormFailureData>;

export type CreateFormRequestActionType = ActionType<FormsActionTypes.CREATE_FORM_REQUEST, ICreateFormRequestData>;
export type CreateFormSuccessActionType = ActionType<FormsActionTypes.CREATE_FORM_SUCCESS, ICreateFormSuccessData>;
export type CreateFormFailureActionType = ActionType<FormsActionTypes.CREATE_FORM_FAILURE, ICreateFormFailureData>;

export type UpdateFormNameRequestActionType = ActionType<
    FormsActionTypes.UPDATE_FORM_NAME_REQUEST,
    IUpdateFormNameRequestData
>;
export type UpdateFormNameSuccessActionType = ActionType<
    FormsActionTypes.UPDATE_FORM_NAME_SUCCESS,
    IUpdateFormNameSuccessData
>;
export type UpdateFormNameFailureActionType = ActionType<
    FormsActionTypes.UPDATE_FORM_NAME_FAILURE,
    IUpdateFormNameFailureData
>;

export type UpdateFormBackgroundRequestActionType = ActionType<
    FormsActionTypes.UPDATE_FORM_BACKGROUND_REQUEST,
    IUpdateFormBackgroundRequestData
>;
export type UpdateFormBackgroundSuccessActionType = ActionType<
    FormsActionTypes.UPDATE_FORM_BACKGROUND_SUCCESS,
    IUpdateFormBackgroundSuccessData
>;
export type UpdateFormBackgroundFailureActionType = ActionType<
    FormsActionTypes.UPDATE_FORM_BACKGROUND_FAILURE,
    IUpdateFormBackgroundFailureData
>;
export type SetFormStylesActionType = ActionType<FormsActionTypes.SET_FORM_STYLES, ISetFormStyles>;

export type UpdateFormPageStylesRequestActionType = ActionType<
    FormsActionTypes.UPDATE_FORM_PAGE_STYLES_REQUEST,
    IUpdateFormPageStylesRequestData
>;
export type UpdateFormPageStylesSuccessActionType = ActionType<
    FormsActionTypes.UPDATE_FORM_PAGE_STYLES_SUCCESS,
    IUpdateFormPageStylesSuccessData
>;
export type UpdateFormPageStylesFailureActionType = ActionType<
    FormsActionTypes.UPDATE_FORM_PAGE_STYLES_FAILURE,
    IUpdateFormPageStylesFailureData
>;

export type SetCurrentFormActionType = ActionType<FormsActionTypes.SET_CURRENT_FORM, ISetCurrentForm>;

export type SetInitialFontsActionType = ActionType<FormsActionTypes.SET_INITIAL_FONTS, ISetInitialFonts>;

export type GetUserFontsRequestActionType = ActionType<
    FormsActionTypes.GET_USER_FONTS_REQUEST,
    IGetUserFontsRequestData
>;
export type GetUserFontsSuccessActionType = ActionType<
    FormsActionTypes.GET_USER_FONTS_SUCCESS,
    IGetUserFontsSuccessData
>;
export type GetUserFontsFailureActionType = ActionType<
    FormsActionTypes.GET_USER_FONTS_FAILURE,
    IGetUserFontsFailureData
>;

export type SaveChangedFontsRequestActionType = ActionType<
    FormsActionTypes.SAVE_CHANGED_FONTS_REQUEST,
    ISaveChangedFontsRequestData
>;
export type SaveChangedFontsSuccessActionType = ActionType<
    FormsActionTypes.SAVE_CHANGED_FONTS_SUCCESS,
    ISaveChangedFontsSuccessData
>;
export type SaveChangedFontsFailureActionType = ActionType<
    FormsActionTypes.SAVE_CHANGED_FONTS_FAILURE,
    ISaveChangedFontsFailureData
>;
export type UpdateFormButtonsDataRequest = ActionType<
    FormsActionTypes.UPDATE_FORM_BUTTONS_DATA_REQUEST,
    IUpdateFormButtonsDataRequest
>;

export type TransferFormActionType = ActionType<FormsActionTypes.TRANSFER_FORM, ITransferForm>;
export type SetTransferFormDataActionType = ActionType<FormsActionTypes.SET_TRANSFER_FORM_DATA, ITransferFormData>;

export type SetTransferFormLoadingActionType = ActionType<FormsActionTypes.SET_TRANSFER_LOADING, ITransferLoading>;

export type TransferFormSubmissionsActionType = ActionType<
    FormsActionTypes.TRANSFER_FORM_SUBMISSIONS,
    ITransferFormSubmissions
>;
